import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { runPublish } from "../lib/publish";
import { Badge, Banner, Button, Card, EmptyState, Mono, PageBody, PageHeader } from "../ui/Field";

type BuildRow = {
  id: string;
  status: "queued" | "running" | "success" | "failed";
  started_at: number | null;
  finished_at: number | null;
  pages_deployment_id: string | null;
  error: string | null;
  created_at: number;
};

export default function Builds() {
  const { slug } = useParams();
  const [list, setList] = useState<BuildRow[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [log, setLog] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [lastUrl, setLastUrl] = useState<string | null>(null);
  const logRef = useRef<HTMLPreElement>(null);

  const load = () => api.get<{ items: BuildRow[] }>(`/api/sites/${slug}/builds`).then((r) => setList(r.items)).catch((e) => setErr((e as Error).message));
  useEffect(() => { if (slug) load(); }, [slug]);

  function appendLog(line: string) {
    setLog((xs) => [...xs, `[${new Date().toLocaleTimeString()}] ${line}`]);
    setTimeout(() => logRef.current?.scrollTo({ top: logRef.current.scrollHeight }), 0);
  }

  async function publish() {
    if (!slug) return;
    setBusy(true);
    setErr(null);
    setLog([]);
    setLastUrl(null);
    try {
      const r = await runPublish(slug, appendLog);
      setLastUrl(r.url);
      appendLog(`✓ Published. Visit: ${r.url}`);
      load();
    } catch (e) {
      appendLog(`✗ ${(e as Error).message}`);
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <PageBody>
      <PageHeader
        eyebrow="№ 08 · Deployments"
        title={<>Builds</>}
        description="One-click publish to Cloudflare Pages. Each build is recorded; older successful deployments can be promoted back to production."
        actions={<Button onClick={publish} disabled={busy}>{busy ? "Publishing…" : "Publish site"}</Button>}
      />
      {err && <Banner kind="error">{err}</Banner>}
      {lastUrl && (
        <Banner kind="success" title="Site published">
          Live at <a href={lastUrl} target="_blank" rel="noreferrer" className="underline">{lastUrl}</a>
        </Banner>
      )}

      {(busy || log.length > 0) && (
        <Card number="L1" title="Build log" description="Streaming output from the publish pipeline.">
          <pre
            ref={logRef}
            className="bg-[#0c0c0c] text-[#9ee87a] font-mono p-4 rounded-[var(--radius-sm)] max-h-80 overflow-auto text-[12px] leading-[1.55] border border-[var(--color-rule)]"
          >
            {log.join("\n")}
          </pre>
        </Card>
      )}

      <Card number="H1" title={`History (${list.length})`} className="mt-6" bodyClassName={list.length === 0 ? "p-5" : "p-0"}>
        {list.length === 0 ? (
          <EmptyState title="No builds yet." description="Press 'Publish site' to start your first deployment." />
        ) : (
          <table className="w-full text-[13.5px] border-collapse">
            <thead>
              <tr className="text-left label-tracked border-b border-[var(--color-rule)]">
                <th className="px-5 py-2.5">ID</th>
                <th className="px-5 py-2.5">Status</th>
                <th className="px-5 py-2.5">Started</th>
                <th className="px-5 py-2.5">Duration</th>
                <th className="px-5 py-2.5">Deployment</th>
              </tr>
            </thead>
            <tbody>
              {list.map((b) => (
                <tr key={b.id} className="border-b border-[var(--color-rule)] last:border-0">
                  <td className="px-5 py-2.5"><Mono>{b.id.slice(-10)}</Mono></td>
                  <td className="px-5 py-2.5"><Badge tone={statusTone(b.status)}>{b.status}</Badge></td>
                  <td className="px-5 py-2.5 text-[12px] text-[var(--color-muted)]">{b.started_at ? new Date(b.started_at).toLocaleString() : ""}</td>
                  <td className="px-5 py-2.5 text-[12px] text-[var(--color-muted)] font-mono">{b.started_at && b.finished_at ? Math.round((b.finished_at - b.started_at) / 1000) + "s" : ""}</td>
                  <td className="px-5 py-2.5 text-[12px] text-[var(--color-muted)]">
                    <span className="font-mono break-all">{b.pages_deployment_id ?? b.error ?? ""}</span>
                    {b.status === "success" && b.pages_deployment_id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-2"
                        onClick={async () => {
                          if (!confirm("Promote this older deployment back to production?")) return;
                          try {
                            await api.post(`/api/sites/${slug}/builds/${b.id}/promote`);
                            alert("Rollback triggered. New deployment created.");
                            load();
                          } catch (e) { alert((e as Error).message); }
                        }}
                      >
                        Promote
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </PageBody>
  );
}

function statusTone(s: BuildRow["status"]): "success" | "info" | "error" | "neutral" {
  return s === "success" ? "success" : s === "running" ? "info" : s === "failed" ? "error" : "neutral";
}
